import { useState, useCallback } from "react";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

interface UploadResult {
  objectPath: string;
  fileName: string;
  contentType: string;
}

interface UseFileUploadOptions {
  onSuccess?: (result: UploadResult) => void;
  onError?: (error: Error) => void;
}

export function useFileUpload(options?: UseFileUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "File type not supported. Please upload an image (JPEG, PNG, GIF, WebP), PDF, or Office document.";
    }
    if (file.size > MAX_SIZE_BYTES) {
      return "File is too large. Maximum size is 10 MB.";
    }
    return null;
  }, []);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        options?.onError?.(new Error(validationError));
        return null;
      }

      setIsUploading(true);
      setError(null);

      try {
        // Step 1: Request a presigned URL
        const metaRes = await fetch(`${BASE_URL}/api/storage/uploads/request-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: file.name,
            size: file.size,
            contentType: file.type,
          }),
          credentials: "include",
        });

        if (!metaRes.ok) {
          throw new Error("Failed to get upload URL");
        }

        const { uploadURL, objectPath } = await metaRes.json();

        // Step 2: Upload directly to GCS
        const uploadRes = await fetch(uploadURL, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });

        if (!uploadRes.ok) {
          throw new Error("Failed to upload file");
        }

        const result: UploadResult = {
          objectPath,
          fileName: file.name,
          contentType: file.type,
        };

        options?.onSuccess?.(result);
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setError(msg);
        options?.onError?.(err instanceof Error ? err : new Error(msg));
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, options],
  );

  return { uploadFile, isUploading, error, setError };
}
